import { testId } from '../../utils';
import { Iconography } from './Iconography';
import clsx from 'clsx';
import React from 'react';

export enum InputType {
  TEXT = 'text',
  PASSWORD = 'password',
  EMAIL = 'email',
  PHONE = 'tel',
}

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> &
  (
    | {
        type: InputType.TEXT | InputType.EMAIL | InputType.PHONE;
        className?: string;
        maxLength?: number;
        invalid?: boolean;
        'data-testid': string;
        onClickClear?: () => void;
      }
    | {
        type: InputType.PASSWORD;
        className?: string;
        maxLength?: number;
        invalid?: boolean;
        'data-testid': string;
        onClickClear?: never;
      }
  );

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      maxLength,
      invalid,
      'data-testid': dataTestId,
      onClickClear,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [currentType, setCurrentType] = React.useState(type);
    type TogglePasswordVisibilityEvent = React.MouseEvent<HTMLButtonElement>;

    const togglePasswordVisibility = (
      event: TogglePasswordVisibilityEvent,
    ): void => {
      event.preventDefault();
      if (currentType === InputType.PASSWORD) {
        setCurrentType(InputType.TEXT);
      } else {
        setCurrentType(InputType.PASSWORD);
      }
      setShowPassword(!showPassword);
    };

    return (
      <div className="relative">
        <input
          {...testId(dataTestId)}
          type={currentType}
          className={clsx(
            'flex w-full px-3 h-12',
            'border hover:border-2 border-gray-light rounded ring-light-white',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'file:border-0 file:bg-transparent file:text-sm file:font-medium',
            'focus:bg-white focus:outline-blue-dark',
            {
              'border-red-dark bg-red-light': invalid,
              'pr-12': type === InputType.PASSWORD || !!onClickClear,
              'bg-gray-lightest': !props.value,
            },
            className,
          )}
          ref={ref}
          maxLength={maxLength}
          {...props}
        />
        {onClickClear && props.value && (
          <button
            {...testId(dataTestId, 'input-clear')}
            type="button"
            className="absolute inset-0 left-auto p-3 text-sm text-gray-darkest"
            onClick={onClickClear}
            aria-label="Clear input"
          >
            <Iconography className="block" variant="close" />
          </button>
        )}
        {type === InputType.PASSWORD && (
          <button
            {...testId(dataTestId, 'password-visibility')}
            type="button"
            className="absolute inset-0 left-auto p-3 text-sm text-gray-darkest"
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Iconography
              className="text-gray-darkest"
              aria-hidden
              variant={showPassword ? 'visibility_off' : 'visibility'}
            />
          </button>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';
